import { httpRequest } from '@/lib/http-client';

export interface DocumentFile {
  id: string;
  order_id: string;
  file_name: string;
  file_path: string;
  content_type: string | null;
  file_size: number | null;
  created_at: string;
  created_by: string | null;
}

export interface ConsolidationResultFormat {
  consolidatedEntities: any[];
  rawEntities: any[];
  metadata: {
    sourceFiles: string[];
    processingTime: number;
    timestamp: string;
  };
  extendedData?: any;
}

export interface EntitySnapshot {
  id: string;
  order_id: string;
  created_at: string;
  entities: ConsolidationResultFormat | any[];
}

export interface ComplianceSnapshot {
  id: string;
  order_id: string;
  status: string;
  entity_count: number;
  flagged_count: number;
  opensanctions_results?: any;
  compliance_results?: any;
  created_at: string;
}

export interface OrderEntity {
  id: string;
  order_id: string;
  entity_id: string;
  entity_type: string;
  name: string;
  status: string;
  created_at: string;
}

export interface ScreenedEntity {
  name: string;
  type: string;
  flagged: boolean;
  topMatch?: { name: string; score: number; topics: string[] };
}

/** Screening across every file in an order. */
export interface OrderScreening {
  status: 'flagged' | 'clear' | 'none';
  screenedCount: number;
  flaggedCount: number;
  entities: ScreenedEntity[];
}

export class OrderHistoryService {
  private static baseUrl = '/api/order-history';

  /**
   * Get all documents for an order
   */
  static async getOrderDocuments(orderId: string, token: string): Promise<DocumentFile[]> {
    console.log('Fetching order documents:', { orderId });
    
    try {
      const documents = await httpRequest<DocumentFile[]>(
        `${this.baseUrl}/documents/${orderId}`,
        { method: 'GET' },
        token
      );
      
      console.log('Documents fetched successfully:', { count: documents.length });
      return documents;
    } catch (error) {
      if (error.response?.status === 404 || error?.statusCode === 404 || (error?.message && error.message.includes('not found'))) {
        console.log('No documents found for order:', { orderId });
        return [];
      }
      
      console.error('Error fetching order documents:', error);
      throw error;
    }
  }

  /**
   * Upload a document for an order
   */
  static async uploadDocument(orderId: string, file: File, token: string): Promise<DocumentFile> {
    console.log('Uploading document:', { orderId, fileName: file.name, size: file.size });
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const document = await httpRequest<DocumentFile>(
        `${this.baseUrl}/documents/${orderId}`,
        {
          method: 'POST',
          data: formData,
          headers: {
            // Don't set Content-Type, it will be set automatically with the boundary
          }
        },
        token
      );
      
      console.log('Document uploaded successfully:', { id: document.id, fileName: document.file_name });
      return document;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }


  /**
   * Get compliance snapshot for an order
   */
  static async getComplianceSnapshot(orderId: string, token: string): Promise<ComplianceSnapshot | null> {
    console.log('Fetching compliance snapshot:', { orderId });
    
    try {
      const snapshot = await httpRequest<ComplianceSnapshot>(
        `${this.baseUrl}/compliance/${orderId}`,
        { method: 'GET' },
        token
      );
      
      console.log('Compliance snapshot fetched successfully:', { 
        id: snapshot.id, 
        status: snapshot.status 
      });
      
      return snapshot;
    } catch (error) {
      if (error.response?.status === 404 || error?.statusCode === 404 || (error?.message && error.message.includes('not found'))) {
        console.log('No compliance snapshot found for order:', { orderId });
        return null;
      }
      
      console.error('Error fetching compliance snapshot:', error);
      throw error;
    }
  }

  /**
   * Create a compliance snapshot for an order
   */
  static async createComplianceSnapshot(
    orderId: string, 
    data: {
      status: string;
      entity_count: number;
      flagged_count: number;
      opensanctions_results?: any;
    }, 
    token: string
  ): Promise<ComplianceSnapshot> {
    console.log('Creating compliance snapshot:', { orderId, status: data.status });
    
    try {
      const snapshot = await httpRequest<ComplianceSnapshot>(
        `${this.baseUrl}/compliance/${orderId}`,
        {
          method: 'POST',
          data: data,
          headers: {
            'Content-Type': 'application/json'
          }
        },
        token
      );
      
      console.log('Compliance snapshot created successfully:', { 
        id: snapshot.id, 
        status: snapshot.status 
      });
      
      return snapshot;
    } catch (error) {
      console.error('Error creating compliance snapshot:', error);
      throw error;
    }
  }

  /**
   * Get all entities for an order
   * Returns the entire snapshot object, not just the entities
   */
  static async getOrderEntities(orderId: string, token: string): Promise<any> {
    console.log('Fetching order entities:', { orderId });
    
    try {
      // Get the entity snapshot
      const giveUpAt = Date.now() + 60_000;
      let snapshot: EntitySnapshot;
      for (;;) {
        try {
          snapshot = await httpRequest<EntitySnapshot>(
            `${this.baseUrl}/entities-snapshot/${orderId}`,
            { method: 'GET', headers: { Authorization: `Bearer ${token}` }, timeout: Math.min(15_000, Math.max(1, giveUpAt - Date.now())) }
          );
          break;
        } catch (error) {
          const code = error?.response?.status;
          if ((code && code >= 400 && code < 500) || Date.now() >= giveUpAt) throw error;
          await new Promise(resolve => setTimeout(resolve, Math.min(3_000, giveUpAt - Date.now())));
        }
      }
      
      console.log('Entity snapshot API response received:', {
        hasEntities: !!snapshot.entities,
        snapshotKeys: Object.keys(snapshot),
        entitiesType: typeof snapshot.entities,
        isEntitiesArray: Array.isArray(snapshot.entities)
      });
      
      // Add detailed debugging logs to match server-side
      console.log('SNAPSHOT DEBUG - Client received snapshot:', {
        id: snapshot.id,
        order_id: snapshot.order_id,
        created_at: snapshot.created_at,
        entities_type: typeof snapshot.entities,
        entities_is_array: Array.isArray(snapshot.entities),
        entities_keys: snapshot.entities && typeof snapshot.entities === 'object' && !Array.isArray(snapshot.entities) 
          ? Object.keys(snapshot.entities) 
          : 'N/A',
        has_extended_data: snapshot.entities && 
          typeof snapshot.entities === 'object' && 
          !Array.isArray(snapshot.entities) && 
          'extendedData' in snapshot.entities,
        response_size: JSON.stringify(snapshot).length,
        raw_response_preview: JSON.stringify(snapshot).substring(0, 200) + '...'
      });
      
      // If entities is an object with properties, show its top-level keys
      if (snapshot.entities && typeof snapshot.entities === 'object' && !Array.isArray(snapshot.entities)) {
        console.log('Entities object structure:', { 
          keys: Object.keys(snapshot.entities),
          hasExtendedData: !!snapshot.entities.extendedData,
          extendedDataKeys: snapshot.entities.extendedData ? Object.keys(snapshot.entities.extendedData) : [],
          hasPreservedDocs: !!snapshot.entities.extendedData?.preservedDocumentDetails,
          preservedDocsCount: snapshot.entities.extendedData?.preservedDocumentDetails?.documents?.length || 0
        });
        
        // Log sample document details if available
        if (snapshot.entities.extendedData?.preservedDocumentDetails?.documents?.length > 0) {
          const sampleDoc = snapshot.entities.extendedData.preservedDocumentDetails.documents[0];
          console.log('Sample document details:', {
            fileName: sampleDoc.fileName,
            documentType: sampleDoc.documentType,
            hasMetadata: !!sampleDoc.documentMetadata,
            hasSpecificDetails: !!sampleDoc.documentSpecificDetails
          });
        }
        
        // Log the actual structure of consolidated entities if available
        if (snapshot.entities.consolidatedEntities && Array.isArray(snapshot.entities.consolidatedEntities)) {
          const entities = snapshot.entities.consolidatedEntities;
          console.log('FOUND CONSOLIDATED ENTITIES:', {
            count: entities.length,
            firstEntity: entities.length > 0 ? {
              id: entities[0].id,
              type: entities[0].type || entities[0].entityType,
              name: entities[0].name || entities[0].value,
              hasAddresses: Array.isArray(entities[0].addresses) && entities[0].addresses.length > 0,
              hasContacts: Array.isArray(entities[0].contacts) && entities[0].contacts.length > 0
            } : null
          });
        }
      }
      
      // Return the entire snapshot
      console.log('FINAL ENTITY DATA STRUCTURE:', snapshot);
      return snapshot;
    } catch (snapshotError) {
      // A temporary failure is not evidence that the saved result is absent.
      if (snapshotError?.response?.status !== 404) throw snapshotError;
      console.log('Entity snapshot not found, falling back to individual entities...');
      
      try {
        // Fall back to the old method of getting individual entities
        const entities = await httpRequest<OrderEntity[] | EntitySnapshot['entities']>(
          `${this.baseUrl}/entities/${orderId}`,
          { method: 'GET', headers: { Authorization: `Bearer ${token}` }, timeout: 15_000 }
        );
        
        console.log('Order entities fetched successfully:', { count: Array.isArray(entities) ? entities.length : undefined });
        // This endpoint can return a snapshot body if it was saved between the
        // two reads. Preserve it in the envelope used by the saved-result loader.
        return Array.isArray(entities) ? entities : { entities };
      } catch (error) {
        if (error.response?.status === 404) {
          console.log('No entities found for order:', { orderId });
          return [];
        }
        
        console.error('Error fetching order entities:', error);
        throw error;
      }
    }
  }

  /**
   * Link an entity to an order
   */
  static async linkEntityToOrder(
    orderId: string, 
    entityData: {
      entity_type: string;
      name: string;
      status?: string;
    }, 
    token: string
  ): Promise<OrderEntity> {
    console.log('Linking entity to order:', { 
      orderId, 
      type: entityData.entity_type,
      name: entityData.name 
    });
    
    try {
      const entity = await httpRequest<OrderEntity>(
        `${this.baseUrl}/entities/${orderId}`,
        {
          method: 'POST',
          data: entityData,
          headers: {
            'Content-Type': 'application/json'
          }
        },
        token
      );
      
      console.log('Entity linked successfully:', { 
        id: entity.id, 
        status: entity.status 
      });
      
      return entity;
    } catch (error) {
      console.error('Error linking entity to order:', error);
      throw error;
    }
  }

  /**
   * Update entity status
   */
  static async updateEntityStatus(
    orderId: string,
    entityId: string,
    status: string,
    token: string
  ): Promise<OrderEntity> {
    console.log('Updating entity status:', { orderId, entityId, status });
    
    try {
      const entity = await httpRequest<OrderEntity>(
        `${this.baseUrl}/entities/${orderId}/${entityId}`,
        {
          method: 'PATCH',
          data: { status },
          headers: {
            'Content-Type': 'application/json'
          }
        },
        token
      );
      
      console.log('Entity status updated successfully:', { 
        id: entity.id, 
        status: entity.status 
      });
      
      return entity;
    } catch (error) {
      console.error('Error updating entity status:', error);
      throw error;
    }
  }

  static getOrderScreening(orderId: string): Promise<OrderScreening> {
    return httpRequest<OrderScreening>(`${this.baseUrl}/screening/${encodeURIComponent(orderId)}`);
  }

  /** Saves an uploaded file to the user's device. */
  static async downloadDocument(document: DocumentFile): Promise<void> {
    const file = await httpRequest<Blob>(
      `${this.baseUrl}/documents/${encodeURIComponent(document.order_id)}/${encodeURIComponent(document.id)}/download`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(file);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.file_name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
